import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaHeart, FaComment, FaShare, FaClock, FaUsers, FaChartBar } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';

const RecipeCard = ({ recipe }) => {
  const { isAuthenticated } = useAuth();
  const [isLiked, setIsLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(recipe.likes);

  const handleLike = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAuthenticated) {
      // Could redirect to login or show a modal
      return;
    }
    
    setIsLiked(!isLiked);
    setLikesCount(prev => isLiked ? prev - 1 : prev + 1);
  };

  const handleShare = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (navigator.share) {
      navigator.share({
        title: recipe.title,
        text: `Check out this amazing recipe: ${recipe.title}`,
        url: `${window.location.origin}/recipe/${recipe.id}`,
      });
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(`${window.location.origin}/recipe/${recipe.id}`);
      // You could show a toast notification here
    }
  };

  const getDifficultyStyle = (difficulty) => {
    const map = {
      easy: { color: '#166534', bg: '#dcfce7' },
      medium: { color: '#854d0e', bg: '#fef9c3' },
      hard: { color: '#991b1b', bg: '#fee2e2' },
    };
    const m = map[difficulty.toLowerCase()] || { color: '#374151', bg: '#f3f4f6' };
    return { background: m.bg, color: m.color };
  };

  return (
    <motion.div whileHover={{ y: -5 }} transition={{ duration: 0.2 }} className="card" style={{ overflow: 'hidden', padding: 0 }}>
      <Link to={`/recipe/${recipe.id}`} className="block" style={{ color: 'inherit', textDecoration: 'none' }}>
        {/* Recipe Image */}
        <div style={{ position: 'relative', height: 192, overflow: 'hidden' }}>
          <img src={recipe.image} alt={recipe.title} style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform .3s' }} />
          <div style={{ position: 'absolute', top: 12, right: 12 }}>
            <span style={{ ...getDifficultyStyle(recipe.difficulty), padding: '4px 8px', borderRadius: 999, fontSize: 12, fontWeight: 700 }}>
              {recipe.difficulty}
            </span>
          </div>
          <div style={{ position: 'absolute', bottom: 12, left: 12, display: 'flex', gap: 8 }}>
            {recipe.tags.slice(0, 2).map((tag, index) => (
              <span key={index} style={{ padding: '4px 8px', background: 'rgba(0,0,0,0.7)', color: '#fff', fontSize: 12, borderRadius: 999 }}>
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* Recipe Content */}
        <div style={{ padding: 16 }}>
          {/* Title and Description */}
          <h3 style={{ fontSize: 18, fontWeight: 800, color: '#111827', margin: '0 0 6px' }}>{recipe.title}</h3>
          <p style={{ color: '#6b7280', fontSize: 14, margin: '0 0 12px' }}>{recipe.description}</p>

          {/* Recipe Meta */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, color: '#6b7280', fontSize: 14, marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <FaClock style={{ color: 'var(--brand-600)' }} />
              <span>{recipe.cookTime}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <FaUsers style={{ color: '#1d4ed8' }} />
              <span>{recipe.servings}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <FaChartBar style={{ color: '#6d28d9' }} />
              <span>{recipe.nutrition.calories}</span>
            </div>
          </div>

          {/* Author */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <img src={recipe.author.avatar} alt={recipe.author.name} style={{ width: 32, height: 32, borderRadius: 999 }} />
            <div>
              <p style={{ margin: 0, fontWeight: 600, color: '#111827', fontSize: 14 }}>{recipe.author.name}</p>
              <p style={{ margin: 0, color: '#6b7280', fontSize: 12 }}>{recipe.author.recipes} recipes</p>
            </div>
          </div>

          {/* Nutrition Highlights */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 12 }}>
            <div style={{ textAlign: 'center', padding: 8, background: '#f9fafb', borderRadius: 10 }}>
              <p style={{ margin: 0, color: '#6b7280', fontSize: 12 }}>Protein</p>
              <p style={{ margin: 0, color: '#111827', fontWeight: 600, fontSize: 14 }}>{recipe.nutrition.protein}</p>
            </div>
            <div style={{ textAlign: 'center', padding: 8, background: '#f9fafb', borderRadius: 10 }}>
              <p style={{ margin: 0, color: '#6b7280', fontSize: 12 }}>Carbs</p>
              <p style={{ margin: 0, color: '#111827', fontWeight: 600, fontSize: 14 }}>{recipe.nutrition.carbs}</p>
            </div>
            <div style={{ textAlign: 'center', padding: 8, background: '#f9fafb', borderRadius: 10 }}>
              <p style={{ margin: 0, color: '#6b7280', fontSize: 12 }}>Fat</p>
              <p style={{ margin: 0, color: '#111827', fontWeight: 600, fontSize: 14 }}>{recipe.nutrition.fat}</p>
            </div>
          </div>
        </div>
      </Link>

      {/* Action Buttons */}
      <div style={{ padding: '0 16px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 16, borderTop: '1px solid #f3f4f6' }}>
          <button onClick={handleLike} className="btn" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 10, background: isLiked ? '#fee2e2' : '#f3f4f6', color: isLiked ? '#b91c1c' : '#6b7280' }}>
            <FaHeart />
            <span style={{ fontSize: 14, fontWeight: 600 }}>{likesCount}</span>
          </button>

          <Link to={`/recipe/${recipe.id}#comments`} className="btn" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 10, background: '#f3f4f6', color: '#6b7280' }}>
            <FaComment />
            <span style={{ fontSize: 14, fontWeight: 600 }}>{recipe.comments}</span>
          </Link>

          <button onClick={handleShare} className="btn" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 10, background: '#f3f4f6', color: '#6b7280' }}>
            <FaShare />
            <span style={{ fontSize: 14, fontWeight: 600 }}>{recipe.shares}</span>
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default RecipeCard;
